/**
 * Turning a print document into ESC/POS bytes, in the browser.
 *
 * The desktop driver does this in Python, on the machine the printer is
 * plugged into. A phone has no such machine: it talks to a Bluetooth printer
 * through an app, and that app wants finished bytes. So the same layout rules
 * live here too — the width, the wrapping, the fact that a figure is never
 * broken across lines.
 *
 * The duplication is deliberate and bounded: both sides render the same block
 * document, the rules are few, and the tests below pin them. The alternative —
 * asking a server to render — would mean a phone at a counter with no signal
 * cannot print, which is exactly when a receipt is wanted.
 */

import type { PrintBlock, PrintDocument } from '@/lib/printing';

/** Printable columns on 58mm paper at Font A. */
export const WIDTH_58 = 32;
/** And on 80mm, which the same code prints if a shop moves up to one. */
export const WIDTH_80 = 48;

const ESC = 0x1b;
const GS = 0x1d;

const INIT = [ESC, 0x40];
const CODEPAGE_858 = [ESC, 0x74, 0x13];
const ALIGN_LEFT = [ESC, 0x61, 0x00];
const ALIGN_CENTER = [ESC, 0x61, 0x01];
const SIZE_NORMAL = [GS, 0x21, 0x00];
const SIZE_DOUBLE = [GS, 0x21, 0x11];
const BOLD_ON = [ESC, 0x45, 0x01];
const BOLD_OFF = [ESC, 0x45, 0x00];
// ESC d 3, not three newlines: the driver sends the command, and a receipt
// must not change shape depending on which of the two rendered it.
const FEED_3 = [ESC, 0x64, 0x03];
const CUT = [GS, 0x56, 0x42, 0x00];

/**
 * Characters a thermal printer cannot render, and what to print instead.
 *
 * An unrenderable byte comes out as a random glyph, which on a receipt reads
 * as corruption rather than as a font problem.
 */
const SUBSTITUTIONS: Record<string, string> = {
  '—': '-',
  '–': '-',
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
  '…': '...',
  '×': 'x',
  '·': '-',
  '÷': '/',
  '≤': '<=',
  '≥': '>=',
  ' ': ' ',
};

export function encodeText(text: string): string {
  let out = '';
  for (const character of String(text)) {
    const replacement = SUBSTITUTIONS[character];
    if (replacement !== undefined) {
      out += replacement;
      continue;
    }
    const code = character.codePointAt(0) ?? 63;
    out += code <= 0xff ? character : '?';
  }
  return out;
}

/** Wrap on spaces, breaking a word only when it cannot fit alone. */
export function wrap(text: string, width: number): string[] {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  for (let word of words) {
    while (word.length > width) {
      if (current) {
        lines.push(current);
        current = '';
      }
      lines.push(word.slice(0, width));
      word = word.slice(width);
    }
    if (!current) current = word;
    else if (current.length + 1 + word.length <= width) current += ` ${word}`;
    else {
      lines.push(current);
      current = word;
    }
  }

  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

/**
 * A label on the left and a figure hard right.
 *
 * The figure is never broken: when the two cannot share a line the label wraps
 * above it, because the customer checks the number first.
 */
export function pair(label: string, value: string, width: number): string[] {
  const left = String(label);
  const right = String(value);

  if (left.length + 1 + right.length <= width) {
    return [left + ' '.repeat(width - left.length - right.length) + right];
  }

  const lines = wrap(left, width);
  const last = lines[lines.length - 1];

  if (last.length + 1 + right.length <= width) {
    lines[lines.length - 1] = last + ' '.repeat(width - last.length - right.length) + right;
    return lines;
  }

  // Nothing fits beside it, so the figure gets its own line, right-aligned.
  lines.push(right.length >= width ? right : ' '.repeat(width - right.length) + right);
  return lines;
}

export function centre(text: string, width: number): string[] {
  return wrap(text, width).map((line) => {
    const padding = Math.floor((width - line.length) / 2);
    return padding > 0 ? ' '.repeat(padding) + line : line;
  });
}

function widthFor(document: PrintDocument): number {
  if (document.width) return document.width;
  return String(document.template ?? '').endsWith('80') ? WIDTH_80 : WIDTH_58;
}

interface Line {
  text: string;
  double: boolean;
}

/** A line item: what it was, then how many at what price for how much. */
function renderItem(block: Extract<PrintBlock, { type: 'item' }>, width: number): Line[] {
  const lines: Line[] = wrap(block.label, width).map((text) => ({ text, double: false }));

  if (block.sku) lines.push({ text: String(block.sku).slice(0, width), double: false });

  const left =
    block.quantity !== undefined && block.price !== undefined
      ? `${block.quantity} x ${block.price}`
      : block.quantity !== undefined
        ? String(block.quantity)
        : '';

  if (block.total !== undefined) {
    for (const text of pair(left, String(block.total), width)) lines.push({ text, double: false });
  } else if (left) {
    lines.push({ text: left.slice(0, width), double: false });
  }

  return lines;
}

/** The document as lines of text, with the ones that print at double size. */
export function renderLines(document: PrintDocument): Line[] {
  const width = widthFor(document);
  const lines: Line[] = [];

  for (const block of document.blocks ?? []) {
    switch (block.type) {
      case 'center':
        for (const text of centre(block.text, width)) lines.push({ text, double: false });
        break;
      case 'pair':
        for (const text of pair(block.label, block.value, width)) {
          lines.push({ text, double: false });
        }
        break;
      case 'rule':
        lines.push({ text: (block.char ?? '-').slice(0, 1).repeat(width), double: false });
        break;
      case 'big':
        if (block.label) {
          for (const text of centre(block.label, width)) lines.push({ text, double: false });
        }
        // Double-size glyphs are twice as wide, so the figure gets its own line.
        lines.push({ text: block.text, double: true });
        break;
      case 'item':
        lines.push(...renderItem(block, width));
        break;
      case 'blank':
        lines.push({ text: '', double: false });
        break;
      case 'signature': {
        const label = block.label ?? 'Signature';
        lines.push({ text: '', double: false });
        lines.push({
          text: `${label} ${'_'.repeat(Math.max(4, width - label.length - 2))}`,
          double: false,
        });
        break;
      }
      default:
        // An unknown block prints as its text rather than being dropped: a
        // receipt missing a line nobody notices is worse than a plain one.
        for (const text of wrap((block as { text?: string }).text ?? '', width)) {
          lines.push({ text, double: false });
        }
    }
  }

  return lines;
}

/** The whole receipt as ESC/POS bytes, ready for a printer. */
export function toEscPos(
  document: PrintDocument,
  options: { cut?: boolean; copies?: number } = {},
): Uint8Array {
  const bytes: number[] = [];
  const push = (values: number[]): void => {
    for (const value of values) bytes.push(value);
  };
  const text = (value: string): void => {
    for (const character of encodeText(value)) bytes.push(character.charCodeAt(0) & 0xff);
  };

  push(INIT);
  push(CODEPAGE_858);

  for (const line of renderLines(document)) {
    // A line the layout padded is meant to sit centred; the printer does that
    // better than spaces do, which matters at double size.
    const centred = line.double || line.text !== line.text.replace(/^\s+/, '');

    push(centred ? ALIGN_CENTER : ALIGN_LEFT);
    if (line.double) {
      push(SIZE_DOUBLE);
      push(BOLD_ON);
    }
    text(centred ? line.text.trim() : line.text);
    if (line.double) {
      push(SIZE_NORMAL);
      push(BOLD_OFF);
    }
    bytes.push(0x0a);
  }

  push(FEED_3);
  if (options.cut !== false) push(CUT);

  const once = Uint8Array.from(bytes);
  const copies = Math.max(1, options.copies ?? 1);
  if (copies === 1) return once;

  const repeated = new Uint8Array(once.length * copies);
  for (let index = 0; index < copies; index += 1) repeated.set(once, once.length * index);
  return repeated;
}

/** Base64 of the bytes, which is how a printer app takes them off a link. */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  // Chunked: a receipt is small, but String.fromCharCode(...bytes) blows the
  // argument limit on anything long enough to matter.
  for (let index = 0; index < bytes.length; index += 8192) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  }
  return btoa(binary);
}
